// Schematron validation via Saxon-JS executing the compiled EN16931 + XRechnung SEFs, with the
// IBAN correction overlaid (D-017/D-018). Keeps the id+flag extraction byte-for-byte identical
// to the proven spike so the 114-file verdict parity is preserved; additionally captures the
// official <svrl:text> message and location for the findings model (D-029).

import { loadSaxon, loadSef, type SaxonJsApi } from './assets';
import { reevaluateIbanRules } from './iban-check';
import type { Finding, Severity, Syntax } from './types';

const IBAN_RULE_IDS = new Set(['BR-DE-19', 'BR-DE-20']);

interface RawAssert {
  id: string;
  flag: string;
  location: string;
  message: string;
}

export interface SchematronResult {
  findings: Finding[];
  hasNonInformationAssert: boolean;
  /** Non-null iff Saxon-JS threw while running the rules — the document is invalid (D-024). */
  crashed: string | null;
}

function attr(tag: string, name: string): string {
  return (tag.match(new RegExp(`${name}="([^"]*)"`)) ?? [])[1] ?? '';
}

function runSchematron(saxon: SaxonJsApi, sef: unknown, xmlText: string): RawAssert[] {
  const svrl = saxon.transform(
    { stylesheetInternal: sef, sourceText: xmlText, destination: 'serialized' },
    'sync',
  ).principalResult;
  return [...svrl.matchAll(/<svrl:failed-assert\b([^>]*)>([\s\S]*?)<\/svrl:failed-assert>/g)].map((m) => {
    const attrs = m[1];
    const body = m[2];
    const text = (body.match(/<svrl:text>([\s\S]*?)<\/svrl:text>/) ?? [])[1] ?? '';
    return {
      id: attr(attrs, 'id'),
      flag: attr(attrs, 'flag'),
      location: attr(attrs, 'location'),
      message: decodeXml(text).replace(/\s+/g, ' ').trim(),
    };
  });
}

// KoSIT SVRL flags: 'fatal' | 'warning' | 'information'. A step is invalid on any non-information
// assert (D-017). Map to our severity model; an unflagged assert is treated as an error.
function flagToSeverity(flag: string): Severity {
  if (flag === 'fatal') return 'fatal';
  if (flag === 'warning') return 'warning';
  if (flag === 'information') return 'information';
  return 'error';
}

export async function validateSchematron(xmlText: string, syntax: Syntax): Promise<SchematronResult> {
  const saxon = await loadSaxon();
  try {
    const [en, xr] = await Promise.all([
      loadSef(`EN16931-${syntax}.sef.json`),
      loadSef(`XRechnung-${syntax}.sef.json`),
    ]);
    const raw = [...runSchematron(saxon, en, xmlText), ...runSchematron(saxon, xr, xmlText)];

    // Preserve any official message text Saxon produced for the IBAN rules (it over-fires
    // BR-DE-19), so our corrected finding keeps the official wording where available.
    const rawById = new Map<string, RawAssert>();
    for (const a of raw) if (!rawById.has(a.id)) rawById.set(a.id, a);

    // Drop Saxon's own opinion on the two IBAN rules; replace with our JS/BigInt re-evaluation.
    const kept = raw.filter((a) => !IBAN_RULE_IDS.has(a.id));
    const corrected = reevaluateIbanRules(xmlText, syntax);
    const ibanFindings: RawAssert[] = Object.entries(corrected)
      .filter(([, fires]) => fires)
      .map(([id]) => ({
        id,
        flag: 'warning', // BR-DE-19/20 are warning-flagged in the pinned config
        location: rawById.get(id)?.location ?? '',
        message: rawById.get(id)?.message ?? IBAN_FALLBACK_MSG[id] ?? '',
      }));

    const all = [...kept, ...ibanFindings];
    const findings: Finding[] = all.map((a) => ({
      ruleId: a.id,
      severity: flagToSeverity(a.flag),
      officialMessage: a.message,
      ...(a.location ? { location: a.location } : {}),
    }));
    return {
      findings,
      hasNonInformationAssert: all.some((a) => a.flag !== 'information'),
      crashed: null,
    };
  } catch (e) {
    // Saxon-JS's stricter parser can throw on malformed XML (e.g. a bad encoding declaration).
    // Inability to even run the rules is itself sufficient reason to call the file invalid — the
    // safe direction; never treat "couldn't check it" as "it's fine" (D-024).
    // Fall back to the Error object (|| not ??) so an empty message never yields '' — the
    // pipeline treats a non-null crashed as invalid, and '' would read as null-ish downstream.
    const crashed = String((e as Error)?.message || e) || 'Schematron engine error';
    return { findings: [], hasNonInformationAssert: false, crashed };
  }
}

// Fallback official wording for the IBAN rules, used only if Saxon produced no assert to reuse.
const IBAN_FALLBACK_MSG: Record<string, string> = {
  'BR-DE-19':
    '[BR-DE-19] Die Kennung der Kontoverbindung (BT-84) muss bei Zahlung per SEPA-Überweisung eine gültige IBAN sein.',
  'BR-DE-20':
    '[BR-DE-20] Die Kennung der Kontoverbindung des Zahlers (BT-91) muss bei SEPA-Lastschrift eine gültige IBAN sein.',
};

function decodeXml(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&amp;/g, '&');
}
